/**
 * Run N-3 — front-desk visibility of the PracticeHub push, DOM-free.
 *
 * Three states from the compact record the server exposes:
 *   delivered            — success (carries the report version);
 *   retrying at hh:mm    — failed with a scheduled retry, a push in flight, or a
 *                          pre-N-2 failed record (the scheduler picks it up at once);
 *   failed after N       — terminal after MAX attempts; offers Push now.
 * No record → nothing rendered (PracticeHub off, or nothing issued yet).
 */
export interface PushRecordView {
  status: 'pending' | 'success' | 'failed';
  at: number;
  version?: number;
  attempts?: number;
  nextRetryAt?: number;
  terminal?: boolean;
  externalRef?: number;
}

export type PushChipState = 'delivered' | 'retrying' | 'failed';

export interface PushChipView {
  state: PushChipState;
  key: string;
  fallback: string;
  params?: Record<string, string | number>;
  tone: 'done' | 'attention' | 'danger';
  canPushNow: boolean;
}

/** hh:mm, 24h, zero-padded, in the viewer's local time. */
export function formatClock(epochMs: number): string {
  const d = new Date(epochMs);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function pushChipView(rec: PushRecordView | null | undefined, now: number = Date.now()): PushChipView | null {
  if (!rec || typeof rec !== 'object') return null;
  if (rec.status === 'success') {
    return {
      state: 'delivered',
      key: 'pushStatus.delivered',
      fallback: 'Sent to PracticeHub v{{version}}',
      params: { version: rec.version ?? 1 },
      tone: 'done',
      canPushNow: false,
    };
  }
  if (rec.status === 'failed' && rec.terminal === true) {
    return {
      state: 'failed',
      key: 'pushStatus.failed',
      fallback: 'PracticeHub: not sent after {{attempts}} attempts',
      params: { attempts: rec.attempts ?? 0 },
      tone: 'danger',
      canPushNow: true,
    };
  }
  // failed-with-schedule, pre-N-2 failed (due now), or pending in flight
  const when = typeof rec.nextRetryAt === 'number' ? rec.nextRetryAt : now;
  return {
    state: 'retrying',
    key: 'pushStatus.retrying',
    fallback: 'PracticeHub: retrying at {{time}}',
    params: { time: formatClock(when) },
    tone: 'attention',
    canPushNow: false,
  };
}
